// ─── Engine: Post-Processing Effects ─────────────────────────────────────────
// Pure canvas operations. Each function takes (ctx, width, height, params)
// and mutates the canvas in place. Callers provide canvases.

// ─── Glitch ─────────────────────────────────────────────────────────────────
function _glitchRand(n) {
  const x = Math.sin(n) * 43758.5453;
  return x - Math.floor(x);
}

export function applyGlitch(ctx, w, h, { intensity = 0.5, sliceCount = 8, rgbOffset = 3, seed = 0 } = {}) {
  if (intensity <= 0) return;

  const src = ctx.canvas;
  const maxDisplace = w * intensity * 0.15;

  // Displace random horizontal slices
  for (let i = 0; i < sliceCount; i++) {
    const sliceY = Math.floor(_glitchRand(seed + i * 73.1) * h);
    const sliceH = Math.floor(_glitchRand(seed + i * 37.3) * (h / sliceCount) + 2);
    const offset = (_glitchRand(seed + i * 91.7) - 0.5) * 2 * maxDisplace;
    ctx.drawImage(src, 0, sliceY, w, sliceH, offset, sliceY, w, sliceH);
  }

  // RGB channel offset
  if (rgbOffset > 0) {
    const MAX_DIM = 800;
    const scale = Math.min(1, MAX_DIM / Math.max(w, h));
    const sw = Math.round(w * scale);
    const sh = Math.round(h * scale);

    const tmp = document.createElement('canvas');
    tmp.width = sw;
    tmp.height = sh;
    const tmpCtx = tmp.getContext('2d');
    tmpCtx.drawImage(src, 0, 0, sw, sh);

    const imageData = tmpCtx.getImageData(0, 0, sw, sh);
    const srcData = new Uint8ClampedArray(imageData.data);
    const data = imageData.data;
    const off = Math.max(1, Math.round(rgbOffset * scale));

    for (let y = 0; y < sh; y++) {
      for (let x = 0; x < sw; x++) {
        const idx = (y * sw + x) * 4;
        data[idx] = srcData[(y * sw + Math.min(x + off, sw - 1)) * 4];
        data[idx + 2] = srcData[(y * sw + Math.max(x - off, 0)) * 4 + 2];
      }
    }
    tmpCtx.putImageData(imageData, 0, 0);
    ctx.clearRect(0, 0, w, h);
    ctx.drawImage(tmp, 0, 0, w, h);
  }
}

// ─── Wave Distort ───────────────────────────────────────────────────────────
export function applyWaveDistort(ctx, w, h, { amplitude = 20, frequency = 0.02, phase = 0, direction = 'horizontal' } = {}) {
  const src = ctx.canvas;
  const CHUNK = 4;

  if (direction === 'horizontal' || direction === 'both') {
    const tmp = document.createElement('canvas');
    tmp.width = w; tmp.height = h;
    const tmpCtx = tmp.getContext('2d');
    tmpCtx.drawImage(src, 0, 0);
    ctx.clearRect(0, 0, w, h);
    for (let y = 0; y < h; y += CHUNK) {
      const ch = Math.min(CHUNK, h - y);
      const offset = Math.sin(y * frequency + phase) * amplitude;
      ctx.drawImage(tmp, 0, y, w, ch, offset, y, w, ch);
    }
  }

  if (direction === 'vertical' || direction === 'both') {
    const tmp = document.createElement('canvas');
    tmp.width = w; tmp.height = h;
    const tmpCtx = tmp.getContext('2d');
    tmpCtx.drawImage(ctx.canvas, 0, 0);
    ctx.clearRect(0, 0, w, h);
    for (let x = 0; x < w; x += CHUNK) {
      const cw = Math.min(CHUNK, w - x);
      const offset = Math.cos(x * frequency + phase) * amplitude;
      ctx.drawImage(tmp, x, 0, cw, h, x, offset, cw, h);
    }
  }
}

// ─── Mirror ─────────────────────────────────────────────────────────────────
export function applyMirror(ctx, w, h, { mode = 'horizontal' } = {}) {
  const src = ctx.canvas;

  if (mode === 'horizontal') {
    ctx.save();
    ctx.translate(w, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(src, 0, 0, w / 2, h, 0, 0, w / 2, h);
    ctx.restore();
  } else if (mode === 'vertical') {
    ctx.save();
    ctx.translate(0, h);
    ctx.scale(1, -1);
    ctx.drawImage(src, 0, 0, w, h / 2, 0, 0, w, h / 2);
    ctx.restore();
  } else if (mode === 'quad') {
    const hw = w / 2, hh = h / 2;
    const tmp = document.createElement('canvas');
    tmp.width = w; tmp.height = h;
    tmp.getContext('2d').drawImage(src, 0, 0);
    ctx.clearRect(0, 0, w, h);
    ctx.drawImage(tmp, 0, 0, hw, hh, 0, 0, hw, hh);
    ctx.save(); ctx.translate(w, 0); ctx.scale(-1, 1);
    ctx.drawImage(tmp, 0, 0, hw, hh, 0, 0, hw, hh); ctx.restore();
    ctx.save(); ctx.translate(0, h); ctx.scale(1, -1);
    ctx.drawImage(tmp, 0, 0, hw, hh, 0, 0, hw, hh); ctx.restore();
    ctx.save(); ctx.translate(w, h); ctx.scale(-1, -1);
    ctx.drawImage(tmp, 0, 0, hw, hh, 0, 0, hw, hh); ctx.restore();
  } else if (mode === 'kaleidoscope') {
    const cx = w / 2, cy = h / 2;
    const sliceAngle = Math.PI / 4;
    const radius = Math.sqrt(cx * cx + cy * cy);
    const tmp = document.createElement('canvas');
    tmp.width = w; tmp.height = h;
    tmp.getContext('2d').drawImage(src, 0, 0);
    ctx.clearRect(0, 0, w, h);
    for (let i = 0; i < 8; i++) {
      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(i * sliceAngle);
      if (i % 2 === 1) ctx.scale(1, -1);
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.lineTo(radius, 0);
      ctx.lineTo(radius * Math.cos(sliceAngle), radius * Math.sin(sliceAngle));
      ctx.closePath();
      ctx.clip();
      ctx.drawImage(tmp, -cx, -cy);
      ctx.restore();
    }
  }
}

// ─── Hue Shift ──────────────────────────────────────────────────────────────
export function applyHueShift(ctx, w, h, { shift = 0, saturation = 1, brightness = 1 } = {}) {
  ctx.save();
  ctx.globalCompositeOperation = 'source-atop';
  ctx.filter = `hue-rotate(${shift}deg) saturate(${saturation}) brightness(${brightness})`;
  ctx.drawImage(ctx.canvas, 0, 0);
  ctx.filter = 'none';
  ctx.restore();
}

// ─── Tile ───────────────────────────────────────────────────────────────────
export function applyTile(ctx, w, h, { columns = 2, rows = 2 } = {}) {
  const cols = Math.max(1, Math.round(columns));
  const rws = Math.max(1, Math.round(rows));
  const tw = w / cols;
  const th = h / rws;
  const tmp = document.createElement('canvas');
  tmp.width = w; tmp.height = h;
  tmp.getContext('2d').drawImage(ctx.canvas, 0, 0);
  ctx.clearRect(0, 0, w, h);
  for (let r = 0; r < rws; r++) {
    for (let c = 0; c < cols; c++) {
      ctx.drawImage(tmp, 0, 0, w, h, c * tw, r * th, tw, th);
    }
  }
}

// ─── Feedback ───────────────────────────────────────────────────────────────
// Returns a feedback state object. Call each frame with current canvas.
export function createFeedbackState() {
  return { canvas: null, ctx: null };
}

export function applyFeedback(ctx, w, h, feedbackState, { decay = 0.85, offsetX = 0, offsetY = 0 } = {}) {
  if (!feedbackState.canvas || feedbackState.canvas.width !== w || feedbackState.canvas.height !== h) {
    feedbackState.canvas = document.createElement('canvas');
    feedbackState.canvas.width = w;
    feedbackState.canvas.height = h;
    feedbackState.ctx = feedbackState.canvas.getContext('2d');
  }

  feedbackState.ctx.globalAlpha = decay;
  feedbackState.ctx.drawImage(feedbackState.canvas, offsetX, offsetY);
  feedbackState.ctx.globalAlpha = 1;
  feedbackState.ctx.drawImage(ctx.canvas, 0, 0);

  ctx.clearRect(0, 0, w, h);
  ctx.drawImage(feedbackState.canvas, 0, 0);
}
